import React, { useState, useEffect } from 'react';
import { Product } from '../../utils/types';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import AppIcon from '../../utils/AppIcon';

interface ProductGridProps {
    products: Product[];
    selectedProduct: Product | null;
    onProductSelect: (product: Product) => void;
    zoomProduct: Product | null;
    setZoomProduct: (product: Product | null) => void;
}

const INITIAL_ITEMS = 32;
const ITEMS_PER_LOAD = 16;

const ProductGrid: React.FC<ProductGridProps> = ({ products, selectedProduct, onProductSelect, zoomProduct, setZoomProduct }) => {
    const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        if (products.length > 0) {
            setDisplayedProducts(products.slice(0, INITIAL_ITEMS));
            setHasMore(products.length > INITIAL_ITEMS);
            setIsLoadingMore(false);
        } else {
            setDisplayedProducts([]);
            setHasMore(false);
            setIsLoadingMore(false);
        }
    }, [products]);

    const loadMore = () => {
        if (!hasMore || isLoadingMore) return;

        setIsLoadingMore(true);

        setTimeout(() => {
            const currentLength = displayedProducts.length;
            const nextItems = products.slice(currentLength, currentLength + ITEMS_PER_LOAD);

            if (nextItems.length > 0) {
                setDisplayedProducts(prev => [...prev, ...nextItems]);
                setHasMore(currentLength + nextItems.length < products.length);
            } else {
                setHasMore(false);
            }

            setIsLoadingMore(false);
        }, 300);
    };

    const { observerRef } = useInfiniteScroll(loadMore, {
        enabled: hasMore && !isLoadingMore
    });

    const LoadMoreIndicator = () => (
        <div className="col-span-full flex justify-center py-4">
            <svg className="animate-spin h-6 w-6 text-blue-500"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        </div>
    );

    return (
        <div className="max-h-[calc(var(--image-row-height)*4)] overflow-y-auto pr-1"
            style={{ '--image-row-height': 'min(calc((100vw - 2rem)/2), 150px)', scrollbarWidth: 'thin', scrollbarColor: '#CBD5E0 #F7FAFC' } as React.CSSProperties}>
            <div className={`${!zoomProduct ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" : ""}`}>
                {!zoomProduct && displayedProducts.map((product) => (
                    <div key={product.id} className='relative'>
                        <div className={`relative cursor-pointer rounded-lg overflow-hidden border-[1px] transition-all ${selectedProduct?.id === product.id
                                ? 'border-blue-500 shadow-md'
                                : 'border-gray-200 hover:border-blue-300'
                                }`}
                            onClick={() => onProductSelect(product)}
                        >
                            <div className="aspect-square relative">
                                <img
                                    src={product.imageUrl}
                                    alt={product.name}
                                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                    onError={(e) => {
                                        const imgElement = e.currentTarget;
                                        imgElement.onerror = null;
                                        imgElement.src = `https://placehold.co/300x300?text=${product.id.substring(0, 8)}`;
                                    }}
                                />
                            </div>
                            {selectedProduct?.id === product.id && (
                                <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setZoomProduct(product)}
                            className="absolute bottom-1 right-1"
                        >
                            <i className="">
                                <AppIcon name="zoom_ico" size={32} />
                            </i>
                        </button>
                    </div>
                ))}

                {!!zoomProduct && (
                    <div className="aspect-square relative">
                        <img
                            src={zoomProduct?.imageUrl}
                            alt={zoomProduct?.name}
                            className="w-full h-full rounded-xl object-cover transition-transform duration-200 group-hover:scale-105"
                            onError={(e) => {
                                const imgElement = e.currentTarget;
                                imgElement.onerror = null;
                                imgElement.src = `https://placehold.co/300x300?text=${zoomProduct.id.substring(0, 8)}`;
                            }}
                        />
                        <button
                            onClick={() => setZoomProduct(null)}
                            className="absolute top-2 right-2"
                        >
                            <i className="">
                                <AppIcon name="close_ico" size={32} />
                            </i>
                        </button>
                    </div>
                )}

                {displayedProducts.length === 0 && (
                    <div className="col-span-full text-center py-8 text-gray-500">
                        No products available.
                    </div>
                )}

                {/* Load more indicator and trigger */}
                {hasMore && displayedProducts.length > 0 && !zoomProduct && (
                    <>
                        {isLoadingMore && <LoadMoreIndicator />}
                        <div ref={observerRef} className="col-span-full h-1" />
                    </>
                )}
            </div>
        </div>
    );
};

export default ProductGrid;